import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@api/api';
import type { AdminProfile, AdminRole } from '@store/slices/adminAuthSlice';

export type AdminLoginResponse = {
  token: string;
  admin: AdminProfile;
};

export type CreateAdminRequest = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
};

export type CreateAdminResponse = {
  admin: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: AdminRole;
    isActive: boolean;
    notificationPreferences: AdminNotificationPreferences;
  };
};

export type AdminNotificationPreferences = {
  newUserRegistration: boolean;
  newSubscriptionPurchase: boolean;
};

export type ManagedAdmin = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: AdminRole;
  isActive: boolean;
  notificationPreferences: AdminNotificationPreferences;
  createdAt: string;
  updatedAt: string;
};

export type AdminListResponse = {
  admins: ManagedAdmin[];
};

export type AdminStatsDailyPoint = { date: string; count: number };
export type AdminPatchTelemetryDailyPoint = {
  date: string;
  generated: number;
  served: number;
  fallback: number;
  skipped: number;
  failed: number;
};
export type AdminPatchTelemetryReason = {
  reason: string;
  count: number;
};
export type AdminPatchTelemetryFailureReason = AdminPatchTelemetryReason & {
  eventType:
    | 'generation_failed'
    | 'apply_failed'
    | 'reconstruction_failed'
    | 'integrity_failed'
    | string;
};

export type AdminPatchTelemetrySkippedReason = AdminPatchTelemetryReason & {
  algorithm: string;
};

export type AdminPatchPipelineHealth = {
  thresholdMs: number;
  stuckPending: {
    total: number;
    unlocked: number;
    expiredLocks: number;
    byAlgorithm: Array<{
      algorithm: string;
      total: number;
      unlocked: number;
      expiredLocks: number;
    }>;
    oldest: Array<{
      targetHash: string;
      algorithm: string;
      kind: 'unlocked' | 'expired_lock';
      ageMs: number;
    }>;
  };
  latestReadyByAlgorithm: Array<{
    algorithm: string;
    readyAt: string;
  }>;
};

export type AdminPatchTelemetryOverview = {
  telemetryAvailable?: boolean;
  totals: {
    generated: number;
    served: number;
    fallback: number;
    skipped: number;
    failed: number;
    generationFailed: number;
    applyFailed: number;
    reconstructionFailed: number;
    integrityFailed: number;
    bytesSaved: number;
  };
  series: AdminPatchTelemetryDailyPoint[];
  skippedTelemetryAvailable: boolean;
  topFallbackReasons: AdminPatchTelemetryReason[];
  topSkippedReasons: AdminPatchTelemetrySkippedReason[];
  topFailureReasons: AdminPatchTelemetryFailureReason[];
  pipelineHealth: AdminPatchPipelineHealth | null;
};

export type AdminOperationComponentState =
  | 'pending'
  | 'active'
  | 'running'
  | 'configured'
  | 'disabled'
  | 'error';

export type AdminOperationRun = {
  jobId: string | null;
  status: 'running' | 'completed' | 'failed';
  trigger: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  summary: string | null;
  requestContext: {
    orgId: string | null;
    projectId: string | null;
    channelId: string | null;
    requestedByAdminEmail: string | null;
  };
};

export type AdminQueueMetrics = {
  waiting: number | null;
  delayed: number | null;
  active: number | null;
  failed: number | null;
  oldestWaitingJobCreatedAt: string | null;
  oldestWaitingJobAgeMs?: number | null;
  health: 'ok' | 'warning' | 'unknown';
  warningReasons?: string[];
};

export type AdminOperationWorkerHeartbeat = {
  platformEnvironment: 'prod' | 'stag';
  processId: string;
  processRole: 'worker';
  componentKey: string;
  name: string;
  description: string;
  category: 'worker' | 'scheduler';
  state: AdminOperationComponentState;
  statusText: string;
  statusUpdatedAt: string;
  details: Record<string, string | number | boolean | null>;
  lastSeenAt: string;
  expiresAt: string;
  ageMs: number;
  fresh: boolean;
};

export type AdminOperationComponent = {
  key: string;
  name: string;
  description: string;
  category: 'dependency' | 'server' | 'socket' | 'worker' | 'scheduler';
  state: AdminOperationComponentState;
  statusText: string;
  updatedAt: string;
  details: Record<string, string | number | boolean | null>;
  queueName: string | null;
  queueMetrics: AdminQueueMetrics | null;
  workerHeartbeat: AdminOperationWorkerHeartbeat | null;
  currentRun: AdminOperationRun | null;
  lastRun: AdminOperationRun | null;
  nextScheduledRunAt: string | null;
  manualTrigger: {
    enabled: boolean;
    mode: 'none' | 'immediate' | 'channel' | 'project' | 'organization';
    label: string | null;
    description: string | null;
  };
};

export type AdminMongoStorageSummary = {
  databaseUsedBytes: number | null;
  dataBytes: number | null;
  indexBytes: number | null;
  planLimitBytes: number | null;
  planRemainingBytes: number | null;
};

export type AdminOperationsOverview = {
  generatedAt: string;
  mongoStorage: AdminMongoStorageSummary;
  components: AdminOperationComponent[];
};

export type TriggerAdminOperationRequest = {
  operationKey: string;
  orgId?: string;
  projectId?: string;
  channelId?: string;
};

export type TriggerAdminOperationResponse = {
  operationKey: string;
  queueName: string;
  jobId: string | null;
  status: 'queued';
};

export type SetTrustMetricsPausedRequest = {
  paused: boolean;
};

export type SetTrustMetricsPausedResponse = {
  operationKey: 'trust-metrics-accumulation';
  paused: boolean;
};

export type AdminOpsAgentTarget = 'staging' | 'production';
export type AdminOpsAgentLogService = 'api' | 'workers' | 'nginx' | 'runner' | 'ops-agent';

export type AdminOpsAgentPayload = unknown;

export type AdminOpsAgentOverview = {
  health?: AdminOpsAgentPayload;
  systemResources?: AdminOpsAgentPayload;
  pm2?: AdminOpsAgentPayload;
  workers?: AdminOpsAgentPayload;
  runner?: AdminOpsAgentPayload;
  redisStaging?: AdminOpsAgentPayload;
  deployments?: AdminOpsAgentPayload;
  [key: string]: AdminOpsAgentPayload | undefined;
};

export type AdminOpsAgentLogsRequest = {
  target: AdminOpsAgentTarget;
  service: AdminOpsAgentLogService;
  lines?: number;
};

export type DeleteAdminUserRequest = {
  userId: string;
  confirmation: string;
};

export type DeleteAdminUserResponse = {
  success: true;
};

export type DeleteAdminProjectRequest = {
  projectId: string;
  confirmation: string;
};

export type DeleteAdminProjectResponse = {
  projectId: string;
  orgId: string;
  status: 'deleting';
  cleanup: {
    mode: 'queued';
    jobId: string | null;
  };
};

export type DeleteAdminOrganizationRequest = {
  orgId: string;
  confirmation: string;
};

export type DeleteAdminOrganizationResponse = {
  orgId: string;
  status: 'deleting';
  cleanup: {
    mode: 'queued';
    jobId: string | null;
  };
};

export type RetainedInstallRecords = {
  method: 'collection-metadata-estimate-v1';
} & (
  | { status: 'available' | 'last-good'; asOf: string }
  | { status: 'unavailable'; asOf: null }
);

export type AdminStatsOverview = {
  contractVersion?: 2;
  snapshotStatus?: 'current' | 'last-good';
  retainedInstallRecords?: RetainedInstallRecords;
  generatedAt: string;
  window?: { start: string; end: string; timeZone: 'UTC'; currentDayIsPartial: true };
  totals: {
    organizations: number;
    projects: number;
    paidSubscriptions: number;
    paidOrganizations: number;
    freeOrganizations: number;
    bundlesUploaded: number;
    installRecords: number | null;
    /** Active bundles plus separately tracked shared assets and patch artifacts. */
    storageOccupiedBytes: number;
    /** Canonical active bundle usage shared with channel, project, billing, and console totals. */
    bundleStorageOccupiedBytes?: number;
    assetStorageOccupiedBytes?: number;
    /** Generated patch bytes, including optional missing-asset archives. */
    patchArtifactStorageOccupiedBytes?: number;
    users: number;
    projectedBillingsThisMonthUsd: number;
    projectedBillingsNextMonthUsd: number;
  };
  series: {
    orgsByDay: AdminStatsDailyPoint[];
    bundlesByDay: AdminStatsDailyPoint[];
    installsByDay: AdminStatsDailyPoint[];
  };
  npm: {
    packages: Array<{
      packageName: string;
      packageUrl: string;
      status: 'available' | 'unavailable';
      errorMessage: string | null;
      rangeStart: string | null;
      rangeEnd: string | null;
      downloadsLastWeek: number | null;
      downloadsLastMonth: number | null;
      averageDailyDownloadsLastMonth: number | null;
      seriesLastMonth: Array<{
        date: string;
        downloads: number;
      }>;
    }>;
  };
  patchTelemetry: AdminPatchTelemetryOverview;
  packageVersionDistribution?: {
    totalBundles: number;
    items: Array<{
      packageVersion: string | null;
      label: string;
      count: number;
      percentage: number;
    }>;
  };
};

type RawAdminPatchTelemetry = {
  totals?: Partial<AdminPatchTelemetryOverview['totals']> | null;
  series?: Array<Omit<AdminPatchTelemetryDailyPoint, 'skipped'> & { skipped?: number }> | null;
  topFallbackReasons?: AdminPatchTelemetryReason[] | null;
  topSkippedReasons?: AdminPatchTelemetrySkippedReason[] | null;
  topFailureReasons?: AdminPatchTelemetryFailureReason[] | null;
  pipelineHealth?: AdminPatchPipelineHealth | null;
};

type RawAdminStatsOverview = Omit<AdminStatsOverview, 'patchTelemetry' | 'retainedInstallRecords' | 'snapshotStatus'> & {
  patchTelemetry?: RawAdminPatchTelemetry | null;
  retainedInstallRecords?: unknown;
  snapshotStatus?: unknown;
};

export const createEmptyPatchTelemetry = (): AdminPatchTelemetryOverview => ({
  telemetryAvailable: false,
  totals: {
    generated: 0,
    served: 0,
    fallback: 0,
    skipped: 0,
    failed: 0,
    generationFailed: 0,
    applyFailed: 0,
    reconstructionFailed: 0,
    integrityFailed: 0,
    bytesSaved: 0,
  },
  series: [],
  skippedTelemetryAvailable: false,
  topFallbackReasons: [],
  topSkippedReasons: [],
  topFailureReasons: [],
  pipelineHealth: null,
});

const normalizePatchTelemetry = (
  telemetry: RawAdminPatchTelemetry | null | undefined
): AdminPatchTelemetryOverview => {
  const empty = createEmptyPatchTelemetry();
  const series = telemetry?.series;
  const topFallbackReasons = telemetry?.topFallbackReasons;
  const topSkippedReasons = telemetry?.topSkippedReasons;
  const topFailureReasons = telemetry?.topFailureReasons;
  const totals = telemetry?.totals;
  const telemetryAvailable = [
    totals?.generated, totals?.served, totals?.fallback, totals?.failed, totals?.bytesSaved,
  ].every(value => typeof value === 'number' && Number.isFinite(value));

  return {
    telemetryAvailable,
    totals: {
      ...empty.totals,
      ...(telemetry?.totals ?? {}),
    },
    series: Array.isArray(series)
      ? series.map(point => ({ ...point, skipped: point.skipped ?? 0 }))
      : empty.series,
    skippedTelemetryAvailable:
      typeof telemetry?.totals?.skipped === 'number' && Array.isArray(topSkippedReasons),
    topFallbackReasons: Array.isArray(topFallbackReasons)
      ? topFallbackReasons
      : empty.topFallbackReasons,
    topSkippedReasons: Array.isArray(topSkippedReasons)
      ? topSkippedReasons
      : empty.topSkippedReasons,
    topFailureReasons: Array.isArray(topFailureReasons)
      ? topFailureReasons
      : empty.topFailureReasons,
    pipelineHealth: telemetry?.pipelineHealth ?? null,
  };
};

const normalizeRetainedInstallRecords = (metadata: unknown, value: number | null): RetainedInstallRecords => {
  if (metadata && typeof metadata === 'object') {
    const measurement = metadata as Record<string, unknown>;
    if (measurement.method === 'collection-metadata-estimate-v1' && value !== null &&
      (measurement.status === 'available' || measurement.status === 'last-good') &&
      typeof measurement.asOf === 'string' && Number.isFinite(Date.parse(measurement.asOf))) {
      return { method: measurement.method, status: measurement.status, asOf: measurement.asOf };
    }
  }
  return { method: 'collection-metadata-estimate-v1', status: 'unavailable', asOf: null };
};

const normalizeStatsOverview = (response: RawAdminStatsOverview): AdminStatsOverview => {
  const count = response.totals.installRecords;
  const value = typeof count === 'number' && Number.isSafeInteger(count) && count >= 0 ? count : null;
  const estimated = response.contractVersion === 2 || response.retainedInstallRecords != null;
  const measurement = estimated ? normalizeRetainedInstallRecords(response.retainedInstallRecords, value) : undefined;
  return {
    ...response,
    totals: { ...response.totals, installRecords: measurement?.status === 'unavailable' ? null : value },
    retainedInstallRecords: measurement,
    snapshotStatus: response.snapshotStatus === 'current' || response.snapshotStatus === 'last-good'
      ? response.snapshotStatus : undefined,
    patchTelemetry: normalizePatchTelemetry(response.patchTelemetry),
  };
};

export type AdminAiUsageRange = '7d' | '30d' | '90d';

export type AdminAiUsageBreakdownRow = {
  key: string;
  requests: number;
  successes: number;
  failures: number;
  retries: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  fileSearchCalls: number;
  estimatedCostUsd: number | null;
};

export type AdminAiUsageRecentAttempt = {
  id: string;
  feature: string;
  orgSlug: string;
  projectSlug: string;
  model: string;
  route: string;
  reasoningEffort: string | null;
  durationMs: number;
  fileSearchCalls: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number | null;
  confidence: string | null;
  retry: boolean;
  success: boolean;
  error: string | null;
  createdAt: string;
};

export type AdminAiUsageOverview = {
  generatedAt: string;
  range: AdminAiUsageRange;
  summary: {
    requests: number;
    successes: number;
    failures: number;
    retries: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    fileSearchCalls: number;
    estimatedCostUsd: number | null;
    averageDurationMs: number | null;
  };
  byModel: AdminAiUsageBreakdownRow[];
  byRoute: AdminAiUsageBreakdownRow[];
  byFeature: AdminAiUsageBreakdownRow[];
  recentAttempts: AdminAiUsageRecentAttempt[];
  missingPricingModels: string[];
};

export type AdminCursorPage<TItem> = {
  items: TItem[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type AdminListQueryParams = {
  limit?: number;
  cursor?: string;
  query?: string;
  access?: string;
  status?: string;
  billingInterval?: string;
  platform?: string;
  storageState?: string;
  environment?: string;
  authProvider?: string;
  activation?: string;
};

export type AdminOrganizationListItem = {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  lastActivityAt: string | null;
  projectCount: number;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  billingInterval: string | null;
  currentPeriodEnd: string | null;
  accessSummary: {
    activeGrant: {
      id: string;
      planKey: string;
      sourceType: 'campaign' | 'admin_override';
      sourceId: string;
      sourceName: string | null;
      sourceSlug: string | null;
      status: string;
      startsAt: string;
      endsAt: string | null;
      revokedAt: string | null;
      createdAt: string;
    } | null;
    latestGrant: {
      id: string;
      planKey: string;
      sourceType: 'campaign' | 'admin_override';
      sourceId: string;
      sourceName: string | null;
      sourceSlug: string | null;
      status: string;
      startsAt: string;
      endsAt: string | null;
      revokedAt: string | null;
      createdAt: string;
    } | null;
    latestCampaignRedemption: {
      campaignId: string;
      campaignName: string | null;
      campaignSlug: string | null;
      redeemedAt: string;
    } | null;
    campaignRedemptionCount: number;
    grantHistory: Array<{
      id: string;
      planKey: string;
      sourceType: 'campaign' | 'admin_override';
      sourceId: string;
      sourceName: string | null;
      sourceSlug: string | null;
      status: string;
      startsAt: string;
      endsAt: string | null;
      revokedAt: string | null;
      createdAt: string;
    }>;
    campaignRedemptions: Array<{
      campaignId: string;
      campaignName: string | null;
      campaignSlug: string | null;
      redeemedAt: string;
    }>;
  };
};

export type AdminProjectListItem = {
  id: string;
  projectId: string;
  name: string;
  slug: string;
  description: string;
  status: string;
  orgId: string;
  orgName: string | null;
  orgSlug: string | null;
  createdAt: string;
  lastActivityAt: string | null;
  bundleCount: number;
  installCount: number;
};

export type AdminUserListItem = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  authProvider: string;
  isActivated: boolean;
  isDeleted: boolean;
  lastLoginAt: string | null;
};

export type AdminSubscriptionListItem = {
  id: string;
  orgId: string;
  orgName: string | null;
  orgSlug: string | null;
  plan: string;
  status: string;
  billingInterval: string | null;
  startedAt: string;
  currentPeriodEnd: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  createdAt: string;
};

export type AdminInvoiceListItem = {
  id: string;
  invoiceNumber: string | null;
  status: string;
  collectionMethod: string;
  currency: string;
  amountDue: number;
  amountPaid: number;
  amountRemaining: number;
  createdAt: string;
  dueDate: string | null;
  paidAt: string | null;
  customerEmail: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  orgId: string | null;
  orgName: string | null;
  orgSlug: string | null;
};

export type AdminPaddleTransactionListItem = {
  id: string;
  invoiceNumber: string | null;
  status: string;
  collectionMode: string;
  currencyCode: string;
  total: string | null;
  grandTotal: string | null;
  tax: string | null;
  subtotal: string | null;
  createdAt: string;
  updatedAt: string;
  billedAt: string | null;
  paddleCustomerId: string | null;
  paddleSubscriptionId: string | null;
  orgId: string | null;
  orgName: string | null;
  orgSlug: string | null;
};

export type AdminPaddleTransactionInvoicePdf = {
  url: string;
};

export type AdminBundleListItem = {
  id: string;
  orgId: string;
  orgName: string | null;
  projectId: string;
  projectName: string | null;
  platform: string;
  version: string;
  hash: string;
  bundleVersion: number;
  runtimeVersion: string;
  authorName: string | null;
  uploadedAt: string;
  expiresAt: string | null;
  r2DeletedAt: string | null;
  r2Size: number | null;
  installedCount: number;
};

export type AdminInstallListItem = {
  id: string;
  orgId: string;
  orgName: string | null;
  projectId: string;
  projectName: string | null;
  channelId: string;
  platform: string;
  runtimeVersion: string;
  installId: string;
  currentHash: string;
  environment: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
};

export type AdminFeatureFlag = {
  id: string;
  key: string;
  environment: string;
  description: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AdminFeatureFlagListResponse = {
  featureFlags: AdminFeatureFlag[];
};

export type AdminRuntimeDeliveryProject = {
  orgId: string;
  projectId: string;
  name: string;
  slug: string;
  policy: 'auto' | 'force_v1';
  effectivePolicy: 'auto' | 'force_v1';
  drainState: string | null;
};

export type AdminRuntimeDeliveryConfiguration = {
  automaticDeliveryEnabled: boolean;
  controlPlaneEnabled: boolean;
  manifestPublicationEnabled: boolean;
  outboxWorkerEnabled: boolean;
  artifactAuthorizationEnabled: boolean;
  telemetryEnabled: boolean;
};

export type AdminRuntimeDeliveryOverviewResponse = {
  runtimeDelivery: {
    readiness: {
      ready: boolean;
      blockers: string[];
      configuration: AdminRuntimeDeliveryConfiguration;
    };
    clientAuthority: {
      state: 'enabled' | 'disabled';
      explicitlyConfigured: boolean;
    };
    artifactCapabilityTtlSeconds: number;
    projects: AdminRuntimeDeliveryProject[];
  };
};

type RawRuntimeDeliveryConfiguration = Partial<AdminRuntimeDeliveryConfiguration> & {
  globalMode?: string;
};

type RawRuntimeDeliveryReadiness = {
  ready: boolean;
  blockers: string[];
  configuration: RawRuntimeDeliveryConfiguration;
};

type RawRuntimeDeliveryProject = Partial<AdminRuntimeDeliveryProject> & {
  orgId: string;
  projectId: string;
  name: string;
  slug: string;
  mode?: string;
  effectiveMode?: string;
};

type RawRuntimeDeliveryOverviewResponse = {
  runtimeDelivery: {
    readiness: RawRuntimeDeliveryReadiness;
    v2Readiness?: RawRuntimeDeliveryReadiness;
    clientAuthority: AdminRuntimeDeliveryOverviewResponse['runtimeDelivery']['clientAuthority'];
    artifactCapabilityTtlSeconds: number;
    projects: RawRuntimeDeliveryProject[];
  };
};

const normalizeRuntimeDeliveryConfiguration = (
  configuration: RawRuntimeDeliveryConfiguration
): AdminRuntimeDeliveryConfiguration => ({
  automaticDeliveryEnabled:
    configuration.automaticDeliveryEnabled ?? configuration.globalMode === 'v2',
  controlPlaneEnabled: Boolean(configuration.controlPlaneEnabled),
  manifestPublicationEnabled: Boolean(configuration.manifestPublicationEnabled),
  outboxWorkerEnabled: Boolean(configuration.outboxWorkerEnabled),
  artifactAuthorizationEnabled: Boolean(configuration.artifactAuthorizationEnabled),
  telemetryEnabled: Boolean(configuration.telemetryEnabled),
});

export const normalizeRuntimeDeliveryOverviewResponse = (
  response: RawRuntimeDeliveryOverviewResponse
): AdminRuntimeDeliveryOverviewResponse => {
  const rawRuntimeDelivery = response.runtimeDelivery;
  const rawReadiness = rawRuntimeDelivery.v2Readiness ?? rawRuntimeDelivery.readiness;

  return {
    runtimeDelivery: {
      readiness: {
        ready: rawReadiness.ready,
        blockers: rawReadiness.blockers,
        configuration: normalizeRuntimeDeliveryConfiguration(rawReadiness.configuration),
      },
      clientAuthority: rawRuntimeDelivery.clientAuthority,
      artifactCapabilityTtlSeconds: rawRuntimeDelivery.artifactCapabilityTtlSeconds,
      projects: rawRuntimeDelivery.projects.map(project => ({
        orgId: project.orgId,
        projectId: project.projectId,
        name: project.name,
        slug: project.slug,
        policy: project.policy ?? (project.mode === 'v2' ? 'auto' : 'force_v1'),
        effectivePolicy:
          project.effectivePolicy ??
          (project.effectiveMode === 'v2' ? 'auto' : 'force_v1'),
        drainState: project.drainState ?? null,
      })),
    },
  };
};

export type AdminAccessProviderConfiguration = {
  environment: string;
  providers: Array<{
    provider: 'stripe' | 'paddle';
    enabled: boolean;
    featureFlagKey: string;
  }>;
  availableCheckoutProviders: Array<'stripe' | 'paddle'>;
  defaultCheckoutProvider: 'stripe' | 'paddle' | null;
};

export type AdminAccessPlanOption = {
  key: string;
  name: string;
};

export type AdminAccessProviderOption = {
  value: 'stripe' | 'paddle';
  label: string;
};

export type AdminAccessCampaignCode = {
  id: string;
  campaignId: string;
  code: string;
  status: 'active' | 'revoked' | 'exhausted' | 'expired';
  maxUses: number;
  usedCount: number;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdminAccessCampaign = {
  id: string;
  name: string;
  slug: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  targetPlanKey: string;
  defaultGrantDurationDays: number | null;
  startsAt: string | null;
  endsAt: string | null;
  maxRedemptions: number | null;
  codePrefix: string | null;
  notes: string;
  redemptionCount: number;
  codes: AdminAccessCampaignCode[];
  createdAt: string;
  updatedAt: string;
};

export type AdminAccessGrant = {
  id: string;
  orgId: string;
  orgName: string | null;
  planKey: string;
  sourceType: 'campaign' | 'admin_override';
  sourceId: string;
  status: 'active' | 'revoked' | 'expired';
  startsAt: string;
  endsAt: string | null;
  revokedAt: string | null;
  createdByAdminId: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
};

export type AdminAccessRedemption = {
  id: string;
  campaignId: string;
  campaignName: string | null;
  campaignSlug: string | null;
  campaignTargetPlanKey: string | null;
  campaignCodeId: string;
  code: string | null;
  orgId: string;
  userId: string;
  grantId: string;
  redeemedAt: string;
  createdAt: string;
  updatedAt: string;
};

export type AdminAccessOverviewResponse = {
  providerConfiguration: AdminAccessProviderConfiguration;
  planOptions: AdminAccessPlanOption[];
  providerOptions: AdminAccessProviderOption[];
  campaigns: AdminAccessCampaign[];
  grants: AdminAccessGrant[];
  redemptions: AdminAccessRedemption[];
};

export type SendAdminInboxEmailRequest = {
  to: string;
  subject: string;
  message: string;
};

export type SendAdminInboxEmailResponse = {
  success: true;
  to: string;
  subject: string;
};

export type AdminInboxMessage = {
  id: string;
  to: string;
  subject: string;
  message: string;
  sentByAdminId: string;
  sentByAdminEmail: string;
  sentAt: string;
  createdAt: string;
  updatedAt: string;
};

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Stats', 'Admins', 'Operations', 'OpsAgent', 'FeatureFlags', 'Access'],
  endpoints: builder => ({
    login: builder.mutation<AdminLoginResponse, { email: string; password: string }>({
      query: body => ({
        url: '/admin/auth/login',
        method: 'POST',
        body,
      }),
    }),
    createAdmin: builder.mutation<CreateAdminResponse, CreateAdminRequest>({
      query: body => ({
        url: '/admin/auth/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Admins'],
    }),
    getAdmins: builder.query<AdminListResponse, void>({
      query: () => ({
        url: '/admin/admins',
      }),
      providesTags: ['Admins'],
    }),
    updateAdminStatus: builder.mutation<
      CreateAdminResponse,
      { adminId: string; isActive: boolean }
    >({
      query: ({ adminId, isActive }) => ({
        url: `/admin/admins/${adminId}/status`,
        method: 'PATCH',
        body: { isActive },
      }),
      invalidatesTags: ['Admins'],
    }),
    updateAdminNotificationPreferences: builder.mutation<
      CreateAdminResponse,
      {
        adminId: string;
        notificationPreferences: Partial<AdminNotificationPreferences>;
      }
    >({
      query: ({ adminId, notificationPreferences }) => ({
        url: `/admin/admins/${adminId}/notification-preferences`,
        method: 'PATCH',
        body: { notificationPreferences },
      }),
      invalidatesTags: ['Admins'],
    }),
    updateAdminPassword: builder.mutation<
      CreateAdminResponse,
      { adminId: string; password: string }
    >({
      query: ({ adminId, password }) => ({
        url: `/admin/admins/${adminId}/password`,
        method: 'POST',
        body: { password },
      }),
      invalidatesTags: ['Admins'],
    }),
    deleteAdmin: builder.mutation<CreateAdminResponse, { adminId: string; confirmation: string }>({
      query: ({ adminId, confirmation }) => ({
        url: `/admin/admins/${adminId}`,
        method: 'DELETE',
        body: { confirmation },
      }),
      invalidatesTags: ['Admins'],
    }),
    getNpmStats: builder.query<AdminStatsOverview['npm'], void>({
      // Legacy servers can answer this unsupported optional route with a user-auth
      // 401. Core admin requests still validate and invalidate the session normally.
      query: () => ({ url: '/admin/stats/npm', skipAuthLogout: true }),
      keepUnusedDataFor: 15 * 60,
    }),
    getStatsOverview: builder.query<AdminStatsOverview, void>({
      query: () => ({
        url: '/admin/stats/overview',
        params: { includeNpm: false, contract: 2 },
      }),
      transformResponse: (response: RawAdminStatsOverview) => normalizeStatsOverview(response),
      providesTags: ['Stats'],
    }),
    getAiUsage: builder.query<
      AdminAiUsageOverview,
      { range?: AdminAiUsageRange; limit?: number } | void
    >({
      query: params => ({
        url: '/admin/ai/usage',
        params,
      }),
      providesTags: ['Stats'],
    }),
    getOperationsOverview: builder.query<AdminOperationsOverview, void>({
      query: () => ({
        url: '/admin/operations/overview',
      }),
      providesTags: ['Operations'],
    }),
    getOpsAgentOverview: builder.query<AdminOpsAgentOverview, void>({
      query: () => ({
        url: '/admin/ops-agent/overview',
      }),
      providesTags: ['OpsAgent'],
    }),
    getOpsAgentSystemResources: builder.query<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/system/resources',
      }),
      providesTags: ['OpsAgent'],
    }),
    getOpsAgentPm2: builder.query<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/pm2',
      }),
      providesTags: ['OpsAgent'],
    }),
    restartOpsAgentPm2Process: builder.mutation<
      AdminOpsAgentPayload,
      { processName: string }
    >({
      query: body => ({
        url: '/admin/ops-agent/pm2/restart',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OpsAgent'],
    }),
    getOpsAgentWorkersStatus: builder.query<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/workers/status',
      }),
      providesTags: ['OpsAgent'],
    }),
    startOpsAgentWorkers: builder.mutation<
      AdminOpsAgentPayload,
      { target: AdminOpsAgentTarget }
    >({
      query: ({ target }) => ({
        url: `/admin/ops-agent/workers/${target}/start`,
        method: 'POST',
      }),
      invalidatesTags: ['OpsAgent', 'Operations'],
    }),
    stopOpsAgentWorkers: builder.mutation<
      AdminOpsAgentPayload,
      { target: AdminOpsAgentTarget }
    >({
      query: ({ target }) => ({
        url: `/admin/ops-agent/workers/${target}/stop`,
        method: 'POST',
      }),
      invalidatesTags: ['OpsAgent', 'Operations'],
    }),
    getOpsAgentRunnerStatus: builder.query<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/runner/status',
      }),
      providesTags: ['OpsAgent'],
    }),
    restartOpsAgentRunner: builder.mutation<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/runner/restart',
        method: 'POST',
      }),
      invalidatesTags: ['OpsAgent'],
    }),
    getOpsAgentRedisStaging: builder.query<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/redis/staging',
      }),
      providesTags: ['OpsAgent'],
    }),
    testOpsAgentNginx: builder.mutation<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/nginx/test',
        method: 'POST',
      }),
      invalidatesTags: ['OpsAgent'],
    }),
    reloadOpsAgentNginx: builder.mutation<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/nginx/reload',
        method: 'POST',
      }),
      invalidatesTags: ['OpsAgent'],
    }),
    getOpsAgentLogs: builder.query<AdminOpsAgentPayload, AdminOpsAgentLogsRequest>({
      query: params => {
        return {
          url: `/admin/ops-agent/logs/${params.target}/${params.service}`,
          params: {
            lines: params?.lines ?? 100,
          },
        };
      },
    }),
    getOpsAgentDeployments: builder.query<AdminOpsAgentPayload, void>({
      query: () => ({
        url: '/admin/ops-agent/deployments',
      }),
      providesTags: ['OpsAgent'],
    }),
    dispatchOpsAgentDeployment: builder.mutation<
      AdminOpsAgentPayload,
      { target: AdminOpsAgentTarget }
    >({
      query: ({ target }) => ({
        url: `/admin/ops-agent/deployments/${target}/dispatch`,
        method: 'POST',
      }),
      invalidatesTags: ['OpsAgent'],
    }),
    getFeatureFlags: builder.query<AdminFeatureFlagListResponse, { query?: string } | void>({
      query: params => ({
        url: '/admin/feature-flags',
        params,
      }),
      providesTags: ['FeatureFlags'],
    }),
    getRuntimeDeliveryControls: builder.query<
      AdminRuntimeDeliveryOverviewResponse,
      { query?: string } | void
    >({
      query: params => ({
        url: '/admin/feature-flags/runtime-delivery',
        params,
      }),
      transformResponse: normalizeRuntimeDeliveryOverviewResponse,
      providesTags: ['FeatureFlags'],
    }),
    updateRuntimeDeliveryClientAuthority: builder.mutation<
      {
        clientAuthority: {
          state: 'enabled' | 'disabled';
          explicitlyConfigured: boolean;
          artifactCapabilityTtlSeconds: number;
          propagation: string;
        };
      },
      { enabled: boolean; confirmation: string }
    >({
      query: body => ({
        url: '/admin/feature-flags/runtime-delivery/client-authority',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['FeatureFlags'],
    }),
    updateRuntimeDeliveryProjectPolicy: builder.mutation<
      { project: AdminRuntimeDeliveryProject },
      { projectId: string; policy: 'auto' | 'force_v1'; confirmation: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/admin/feature-flags/runtime-delivery/projects/${projectId}/policy`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['FeatureFlags'],
    }),
    createFeatureFlag: builder.mutation<
      { featureFlag: AdminFeatureFlag },
      {
        key: string;
        environment: string;
        description?: string;
        enabled?: boolean;
      }
    >({
      query: body => ({
        url: '/admin/feature-flags',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FeatureFlags'],
    }),
    updateFeatureFlag: builder.mutation<
      { featureFlag: AdminFeatureFlag },
      {
        featureFlagId: string;
        key?: string;
        environment?: string;
        description?: string;
        enabled?: boolean;
      }
    >({
      query: ({ featureFlagId, ...body }) => ({
        url: `/admin/feature-flags/${featureFlagId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['FeatureFlags'],
    }),
    deleteFeatureFlag: builder.mutation<{ featureFlag: AdminFeatureFlag }, { featureFlagId: string }>({
      query: ({ featureFlagId }) => ({
        url: `/admin/feature-flags/${featureFlagId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FeatureFlags'],
    }),
    getAccessOverview: builder.query<AdminAccessOverviewResponse, { query?: string } | void>({
      query: params => ({
        url: '/admin/access',
        params,
      }),
      providesTags: ['Access'],
    }),
    updateBillingProviderToggle: builder.mutation<
      {
        configuration: AdminAccessProviderConfiguration;
      },
      {
        provider: 'stripe' | 'paddle';
        enabled: boolean;
      }
    >({
      query: body => ({
        url: '/admin/access/providers/toggles',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Access', 'FeatureFlags'],
    }),
    updateDefaultCheckoutProvider: builder.mutation<
      {
        configuration: AdminAccessProviderConfiguration;
      },
      {
        provider: 'stripe' | 'paddle';
      }
    >({
      query: body => ({
        url: '/admin/access/providers/default',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    createAccessCampaign: builder.mutation<
      { campaign: AdminAccessCampaign },
      Record<string, unknown>
    >({
      query: body => ({
        url: '/admin/access/campaigns',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    updateAccessCampaign: builder.mutation<
      { campaign: AdminAccessCampaign },
      { campaignId: string } & Record<string, unknown>
    >({
      query: ({ campaignId, ...body }) => ({
        url: `/admin/access/campaigns/${campaignId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    deleteAccessCampaign: builder.mutation<
      {
        campaignId: string;
        success: true;
        deletedCodes: number;
        deletedRedemptions: number;
        deletedGrants: number;
      },
      { campaignId: string }
    >({
      query: ({ campaignId }) => ({
        url: `/admin/access/campaigns/${campaignId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Access'],
    }),
    generateAccessCampaignCodes: builder.mutation<
      { codes: AdminAccessCampaignCode[] },
      { campaignId: string; count: number; maxUses?: number; expiresAt?: string }
    >({
      query: ({ campaignId, ...body }) => ({
        url: `/admin/access/campaigns/${campaignId}/codes/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    createAccessCampaignCode: builder.mutation<
      { code: AdminAccessCampaignCode },
      { campaignId: string; code: string; maxUses?: number; expiresAt?: string }
    >({
      query: ({ campaignId, ...body }) => ({
        url: `/admin/access/campaigns/${campaignId}/codes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    updateAccessCampaignCode: builder.mutation<
      { code: AdminAccessCampaignCode },
      { campaignId: string; codeId: string; status: 'active' | 'revoked' }
    >({
      query: ({ campaignId, codeId, ...body }) => ({
        url: `/admin/access/campaigns/${campaignId}/codes/${codeId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    createAccessGrant: builder.mutation<
      { grant: AdminAccessGrant },
      {
        orgId: string;
        planKey: string;
        startsAt?: string;
        endsAt?: string;
        notes?: string;
      }
    >({
      query: body => ({
        url: '/admin/access/grants',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Access'],
    }),
    revokeAccessGrant: builder.mutation<{ grant: AdminAccessGrant }, { grantId: string }>({
      query: ({ grantId }) => ({
        url: `/admin/access/grants/${grantId}/revoke`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Access'],
    }),
    sendAdminInboxEmail: builder.mutation<
      SendAdminInboxEmailResponse,
      SendAdminInboxEmailRequest
    >({
      query: body => ({
        url: '/admin/inbox/send',
        method: 'POST',
        body,
      }),
    }),
    getAdminInboxMessages: builder.query<{ messages: AdminInboxMessage[] }, void>({
      query: () => ({
        url: '/admin/inbox/messages',
      }),
    }),
    triggerOperation: builder.mutation<
      TriggerAdminOperationResponse,
      TriggerAdminOperationRequest
    >({
      query: ({ operationKey, ...body }) => ({
        url: `/admin/operations/${operationKey}/trigger`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Operations'],
    }),
    setTrustMetricsPaused: builder.mutation<
      SetTrustMetricsPausedResponse,
      SetTrustMetricsPausedRequest
    >({
      query: ({ paused }) => ({
        url: `/admin/operations/trust-metrics-accumulation/${paused ? 'pause' : 'resume'}`,
        method: 'POST',
      }),
      invalidatesTags: ['Operations'],
    }),
    getOrganizationsPage: builder.query<AdminCursorPage<AdminOrganizationListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/organizations',
        params,
      }),
    }),
    getProjectsPage: builder.query<AdminCursorPage<AdminProjectListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/projects',
        params,
      }),
    }),
    getUsersPage: builder.query<AdminCursorPage<AdminUserListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/users',
        params,
      }),
    }),
    deleteAdminUser: builder.mutation<DeleteAdminUserResponse, DeleteAdminUserRequest>({
      query: ({ userId, confirmation }) => ({
        url: `/admin/users/${userId}`,
        method: 'DELETE',
        body: { confirmation },
      }),
      invalidatesTags: ['Stats'],
    }),
    deleteAdminProject: builder.mutation<DeleteAdminProjectResponse, DeleteAdminProjectRequest>({
      query: ({ projectId, confirmation }) => ({
        url: `/admin/projects/${projectId}`,
        method: 'DELETE',
        body: { confirmation },
      }),
      invalidatesTags: ['Stats'],
    }),
    deleteAdminOrganization: builder.mutation<
      DeleteAdminOrganizationResponse,
      DeleteAdminOrganizationRequest
    >({
      query: ({ orgId, confirmation }) => ({
        url: `/admin/organizations/${orgId}`,
        method: 'DELETE',
        body: { confirmation },
      }),
      invalidatesTags: ['Stats'],
    }),
    getSubscriptionsPage: builder.query<
      AdminCursorPage<AdminSubscriptionListItem>,
      AdminListQueryParams
    >({
      query: params => ({
        url: '/admin/subscriptions',
        params,
      }),
    }),
    getInvoicesPage: builder.query<AdminCursorPage<AdminInvoiceListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/invoices',
        params,
      }),
    }),
    getPaddleTransactionsPage: builder.query<AdminCursorPage<AdminPaddleTransactionListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/paddle-transactions',
        params,
      }),
    }),
    getPaddleTransactionInvoicePdf: builder.query<AdminPaddleTransactionInvoicePdf, string>({
      query: id => ({
        url: `/admin/paddle-transactions/${id}/invoice-pdf`,
      }),
    }),
    getBundlesPage: builder.query<AdminCursorPage<AdminBundleListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/bundles',
        params,
      }),
    }),
    getInstallsPage: builder.query<AdminCursorPage<AdminInstallListItem>, AdminListQueryParams>({
      query: params => ({
        url: '/admin/installs',
        params,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useCreateAdminMutation,
  useGetAdminsQuery,
  useDeleteAdminMutation,
  useUpdateAdminStatusMutation,
  useUpdateAdminNotificationPreferencesMutation,
  useUpdateAdminPasswordMutation,
  useGetStatsOverviewQuery,
  useGetNpmStatsQuery,
  useGetAiUsageQuery,
  useGetOperationsOverviewQuery,
  useGetOpsAgentOverviewQuery,
  useGetOpsAgentSystemResourcesQuery,
  useGetOpsAgentPm2Query,
  useRestartOpsAgentPm2ProcessMutation,
  useGetOpsAgentWorkersStatusQuery,
  useStartOpsAgentWorkersMutation,
  useStopOpsAgentWorkersMutation,
  useGetOpsAgentRunnerStatusQuery,
  useRestartOpsAgentRunnerMutation,
  useGetOpsAgentRedisStagingQuery,
  useTestOpsAgentNginxMutation,
  useReloadOpsAgentNginxMutation,
  useLazyGetOpsAgentLogsQuery,
  useGetOpsAgentDeploymentsQuery,
  useDispatchOpsAgentDeploymentMutation,
  useGetFeatureFlagsQuery,
  useGetRuntimeDeliveryControlsQuery,
  useUpdateRuntimeDeliveryClientAuthorityMutation,
  useUpdateRuntimeDeliveryProjectPolicyMutation,
  useCreateFeatureFlagMutation,
  useUpdateFeatureFlagMutation,
  useDeleteFeatureFlagMutation,
  useGetAccessOverviewQuery,
  useUpdateBillingProviderToggleMutation,
  useUpdateDefaultCheckoutProviderMutation,
  useCreateAccessCampaignMutation,
  useUpdateAccessCampaignMutation,
  useDeleteAccessCampaignMutation,
  useGenerateAccessCampaignCodesMutation,
  useCreateAccessCampaignCodeMutation,
  useUpdateAccessCampaignCodeMutation,
  useCreateAccessGrantMutation,
  useRevokeAccessGrantMutation,
  useSendAdminInboxEmailMutation,
  useGetAdminInboxMessagesQuery,
  useTriggerOperationMutation,
  useSetTrustMetricsPausedMutation,
  useGetOrganizationsPageQuery,
  useGetProjectsPageQuery,
  useGetUsersPageQuery,
  useDeleteAdminUserMutation,
  useDeleteAdminProjectMutation,
  useDeleteAdminOrganizationMutation,
  useGetSubscriptionsPageQuery,
  useGetInvoicesPageQuery,
  useGetPaddleTransactionsPageQuery,
  useLazyGetPaddleTransactionInvoicePdfQuery,
  useGetBundlesPageQuery,
  useGetInstallsPageQuery,
} = adminApi;
